import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@repo/ui/button';

import { importAudit } from '../api/audit-import';
import { ApiError } from '../api/client';
import { loginPathFor } from '../auth/redirect';
import { useAuth } from '../auth/use-auth';
import { hostOf } from '../browser/target-url';
import { AppLoading } from '../marketing/app-loading';
import { CenteredShell, ShellHeading } from '../marketing/centered-shell';
import { useResearchRoomStore } from '../store/research-room-store';

/**
 * `/rooms/from-audit?auditId=<id>` — where the extension's "Ask AI personas"
 * on a usability or copy audit lands (through `/auth/extension`, so the
 * person is signed in as the extension's user).
 *
 * The server makes the session and its capture from the audit and returns at
 * once; the board then builds the panel and opens the room the way it does
 * for any new session, with the moderator's setup notes (`Arrival`) as a walk
 * leaves them. This page is only the hop between the two.
 */
export function AuditImportPage() {
  const [searchParams] = useSearchParams();
  const auditId = searchParams.get('auditId');
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const setArrival = useResearchRoomStore((s) => s.setArrival);
  const setRoomOpen = useResearchRoomStore((s) => s.setOpen);

  const open = useMutation({
    mutationFn: importAudit,
    onSuccess: ({ pageUrl, sessionId }) => {
      // The same handover a walk makes on /browse: the room opens with Floto
      // narrating its setup while the panel is written, instead of an empty
      // placeholder until the personas are ready.
      setRoomOpen(true);
      setArrival({ host: hostOf(pageUrl), sessionId });
      void navigate(`/sessions/${sessionId}`, {
        replace: true,
        state: { from: 'extension-audit' },
      });
    },
  });

  // StrictMode runs effects twice; the server is idempotent per audit, but
  // one request is still the right number.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !auditId) return;
    started.current = true;
    open.mutate(auditId);
  }, [auditId, open]);

  if (!auditId) {
    return (
      <Problem
        sub="This link is missing the audit to open. Run the audit again from the extension."
        title="Nothing to open"
      />
    );
  }

  if (open.isError) {
    // Canvas keeps whoever was already signed in here over the extension's
    // user (`adoptExtensionSession`), so an audit run as someone else is the
    // one failure worth its own words — and its own way out.
    if (open.error instanceof ApiError && open.error.status === 403) {
      return (
        <Problem
          action={
            <Button
              className="h-[44px] cursor-pointer rounded-button bg-brand px-5 text-ui-lg font-semibold text-white hover:bg-brand-alt"
              onClick={() => {
                void signOut().then(() =>
                  navigate(
                    loginPathFor(`/rooms/from-audit?auditId=${auditId}`),
                    { replace: true },
                  ),
                );
              }}
              type="button"
            >
              Sign out and switch account
            </Button>
          }
          sub="This audit was run from a different Floto account than the one signed in here. Sign in with the account you use in the extension to open it."
          title="Different account"
        />
      );
    }
    return (
      <Problem
        action={
          <Button
            className="h-[44px] cursor-pointer rounded-button bg-brand px-5 text-ui-lg font-semibold text-white hover:bg-brand-alt"
            onClick={() => {
              open.mutate(auditId);
            }}
            type="button"
          >
            Try again
          </Button>
        }
        sub={open.error.message}
        title="Couldn't open this audit"
      />
    );
  }

  return <AppLoading label="Setting up your room…" />;
}

function Problem({
  action,
  sub,
  title,
}: {
  action?: React.ReactNode;
  sub: string;
  title: string;
}) {
  return (
    <CenteredShell>
      <ShellHeading sub={sub}>{title}</ShellHeading>
      {action ? <div className="pt-6">{action}</div> : null}
    </CenteredShell>
  );
}
